import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useTenant from '../../hooks/useTenant';
import useAuth from '../../hooks/useAuth';
import useCapabilities from '../../hooks/useCapabilities';
import { atualizarMeuIdioma } from '../../api/usuarios';
// import { aceitarConviteJurado } from '../../api/jurados';
import Step1IdiomaLGPD from '../onboarding/steps/Step1IdiomaLGPD';
import StepFormularioJurado from './steps/StepFormularioJurado';
import StepConclusaoJurado from './steps/StepConclusaoJurado';
import AuthLayout from '../../layouts/Auth';
import WizardCard from '../../components/WizardCard';
import { CONVITE_JURADO_EMAIL_KEY } from './ConviteAceite';

// Wizard principal
function CadastroJuradoWizard() {
  const { t } = useTranslation();
  const { signOut, user, refreshUser } = useAuth();
  const { refreshBootstrap } = useTenant();
  const { refreshCapabilities } = useCapabilities();

  const uid = user?.clerkUserId || user?.id || '';
  const stepKey = `wizard_jurado_step_${uid}`;

  const [activeStep, setActiveStep] = useState(() => {
    try {
      return parseInt(sessionStorage.getItem(stepKey) || '0', 10);
    } catch {
      return 0;
    }
  });
  const [stepData, setStepData] = useState({ idioma: 'pt-BR', lgpdAceito: false });

  // Limpa o email do convite do sessionStorage após iniciar o cadastro
  // (previne reutilização indevida do link de convite)
  useEffect(() => {
    try {
      sessionStorage.removeItem(CONVITE_JURADO_EMAIL_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const STEPS = [
    t('wizard.steps.0'), // Dados da conta / LGPD
    t('wizard.jurado.step2.label'), // Formulário
    t('wizard.steps.3'), // Conclusão
  ];

  const setStep = (n) => {
    setActiveStep(n);
    try {
      sessionStorage.setItem(stepKey, String(n));
    } catch {
      /* ignore */
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setStep(activeStep - 1);
  };

  const handleNext = () => {
    if (activeStep < STEPS.length - 1) setStep(activeStep + 1);
  };

  const canProceed = () => {
    if (activeStep === 0) return stepData.lgpdAceito;
    return false; // outros steps controlam o próprio avanço
  };

  const handleCadastroSuccess = async () => {
    atualizarMeuIdioma(stepData.idioma).catch(() => {});
    // Persiste step=2 ANTES dos refreshes. Os refreshXxx abaixo podem causar
    // re-mount do wizard (TenantProvider/CapabilityProvider re-renderizam) —
    // sem o sessionStorage atualizado, o useState inicial cairia em 0 e o
    // setStep depois operaria em closure morta, voltando o usuário pro step 0.
    setStep(2);
    // Aguarda refreshes pra que os botões "Ir para o painel" da conclusão
    // não redirecionem de volta pro wizard com bootstrap.proximo.acao stale.
    try {
      if (refreshUser) await refreshUser();
      if (refreshBootstrap) await refreshBootstrap();
      if (refreshCapabilities) await refreshCapabilities();
    } catch {
      /* ignore — UI já está no step de conclusão */
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <Step1IdiomaLGPD data={stepData} onChange={setStepData} />;
      case 1:
        return <StepFormularioJurado onSuccess={handleCadastroSuccess} />;
      case 2:
        return <StepConclusaoJurado idioma={stepData.idioma} />;
      default:
        return null;
    }
  };

  // No step 1 (formulário) e 2 (conclusão), os botões não ficam na barra de navegação
  const showNextButton = activeStep === 0;
  const showNavBar = activeStep !== 2;

  return (
    <>
      <Helmet title={t('Judge Registration')} />
      <AuthLayout
        wide
        footer={
          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={signOut}
            startIcon={<LogoutIcon fontSize="small" />}
            sx={{ color: 'text.secondary' }}
          >
            {t('Logout')}
          </Button>
        }
      >
        <WizardCard
          steps={STEPS}
          activeStep={activeStep}
          actions={
            showNavBar ? (
              <>
                {activeStep > 0 ? (
                  <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                    {t('wizard.back')}
                  </Button>
                ) : (
                  <Box />
                )}
                {showNextButton && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleNext}
                    disabled={!canProceed()}
                  >
                    {activeStep === 0 ? t('wizard.continue') : t('wizard.next')}
                  </Button>
                )}
              </>
            ) : null
          }
        >
          {renderStepContent()}
        </WizardCard>
      </AuthLayout>
    </>
  );
}

export default CadastroJuradoWizard;
